import { useState } from 'react'

import useAppDispatch from '../../../store/hooks/useAppDispatch'
import useAppStore from '../../../store/hooks/useAppStore'
import { clientContextSetCurrentCompanyUserId } from '../../clientContext/store/clientContextSlice'
import { fetchClientContext } from '../../clientContext/store/clientContextThunks'
import isCompanyUserEmailVerified from '../../clientContext/utils/isCompanyUserEmailVerified'
import localStorageUtils from '../../app/utils/localStorageUtils'
import { setAuthProfileSelectionModalType } from '../../auth/store/authSlice'
import { ProfileSelectionModalType } from '../../auth/types'
import useUpdateCheckoutCompanyId from '../../checkout/hooks/useUpdateCheckoutCompanyId'
import { outletCompanyOverrideMenuSelector } from '../../outlet/store/outletSelector'
import {
  setPendingCompanyProfileSwitchMemberId,
  showCompanyMemberVerificationPendingModal,
} from '../store/companySlice'
import { PrivateProfileId } from '../constants'

/**
 * Switches between the private profile and company profiles. The current
 * checkout moves to the new company first; with a company override menu the
 * switch waits for that, so items that aren't on the company menu are caught.
 * An unverified company member is rechecked and shown the verification
 * pending modal if they are still unverified.
 */
export default function useSwitchProfile() {
  const dispatch = useAppDispatch()
  const store = useAppStore()

  const updateCheckoutCompanyIfNeeded = useUpdateCheckoutCompanyId()

  /** The profile being switched to: a company member ID or `PrivateProfileId` */
  const [pendingProfileId, setPendingProfileId] = useState<string>()

  function hasCompanyOverrideMenu() {
    return !!outletCompanyOverrideMenuSelector(store.getState())
  }

  /** Moves the checkout to the profile's company, true if it may switch */
  async function updateCheckoutCompany(memberId: string | null) {
    if (hasCompanyOverrideMenu()) {
      return updateCheckoutCompanyIfNeeded(memberId)
    }

    // No company override menu, switch right away without waiting
    updateCheckoutCompanyIfNeeded(memberId)
    return true
  }

  function setCurrentCompanyUser(memberId: string | null) {
    if (memberId) {
      /** Store the selected company user ID in local storage */
      localStorageUtils.setItem<string>('companyProfileId', memberId)
    } else {
      localStorageUtils.removeItem('companyProfileId')
    }

    dispatch(clientContextSetCurrentCompanyUserId(memberId))
    dispatch(setAuthProfileSelectionModalType(ProfileSelectionModalType.None))
  }

  async function switchToVerifiedCompanyProfile(memberId: string) {
    // Set the pending member ID and wait for it to be processed
    dispatch(setPendingCompanyProfileSwitchMemberId(memberId))

    const canSwitch = await updateCheckoutCompany(memberId)

    if (canSwitch) {
      setCurrentCompanyUser(memberId)
      dispatch(setPendingCompanyProfileSwitchMemberId(null))
    }

    return canSwitch
  }

  async function switchToCompanyProfile(memberId: string, verified: boolean) {
    setPendingProfileId(memberId)

    try {
      if (verified) {
        return await switchToVerifiedCompanyProfile(memberId)
      }

      /** If member not verified, recheck the email verification status */
      try {
        const clientContextResponse = await dispatch(
          fetchClientContext({ backgroundFetch: true }),
        ).unwrap()

        const companyUsers = clientContextResponse?.companyUsers ?? []

        if (companyUsers.length) {
          if (isCompanyUserEmailVerified(companyUsers, memberId)) {
            return await switchToVerifiedCompanyProfile(memberId)
          }

          // If not verified, show the verification pending modal
          dispatch(showCompanyMemberVerificationPendingModal({ memberId }))
        }
      } catch (error) {
        dispatch(showCompanyMemberVerificationPendingModal({ memberId }))
      }

      return false
    } finally {
      setPendingProfileId(undefined)
    }
  }

  async function switchToPrivateProfile() {
    setPendingProfileId(PrivateProfileId)

    try {
      const canSwitch = await updateCheckoutCompany(null)

      if (canSwitch) {
        setCurrentCompanyUser(null)
      }

      return canSwitch
    } finally {
      setPendingProfileId(undefined)
    }
  }

  return {
    switchToCompanyProfile,
    switchToPrivateProfile,
    pendingProfileId,
    isSwitching: !!pendingProfileId,
  }
}
